// "Customers say" — an editorial, Amazon-style summary of recurring themes across
// customer reviews, refreshed manually (see CUSTOMER_SAY_README.md). This is curated
// from the review text, not auto-generated, so keep it honest and grounded.

export type CustomerSaySentiment = 'positive' | 'mixed'

export interface CustomerSayCategory {
    /** Short theme label shown on the chip, e.g. "Build experience". */
    label: string
    /** 'positive' shows an up-right arrow; 'mixed' shows a ~ tilde. */
    sentiment: CustomerSaySentiment
    /** How many reviews mention this theme (drives the count in brackets). */
    count: number
    /** Lowercase substrings used to find matching review snippets + highlight them. */
    keywords: string[]
    /** One-sentence theme summary shown at the top of the theme panel. */
    blurb: string
}

export interface CustomerSayData {
    /** 2-4 sentence overview, written like "Customers love that…". */
    summary: string
    /** Themes, ordered most-mentioned first. */
    categories: CustomerSayCategory[]
    /** ISO date (YYYY-MM-DD) this was last refreshed — for the README cadence. */
    updatedAt: string
}

export const customerSay: CustomerSayData = {
    summary:
        'Customers love that children build the headphones themselves, describing it as a fun, ' +
        'hands-on learning experience that kids are genuinely proud of. They praise the sustainable, ' +
        'repairable design with parts that are easy to swap rather than bin, and they highlight ' +
        'responsive, personal customer service. Many call out the quality and beautiful design, the ' +
        'sound, and the reassurance of the built-in volume limit.',
    categories: [
        { label: 'Build experience', sentiment: 'positive', count: 8, keywords: ['build', 'built', 'building', 'assemble', 'construct', 'put together', 'themselves', 'together'], blurb: 'Customers love that children build the headphones themselves, describing the kit as fun and rewarding, with parts that snap together easily.' },
        { label: 'Kids love it', sentiment: 'positive', count: 6, keywords: ['love', 'proud', 'kids', 'children', 'son', 'daughter', 'enjoy', 'cousin', 'wearing'], blurb: 'Children are delighted with their headphones and proud to wear a pair they helped make.' },
        { label: 'Sustainability & repair', sentiment: 'positive', count: 6, keywords: ['sustainab', 'repair', 'replace', 'recycl', 'circular', 'fix', 'spare', 'parts', 'planet', 'environment', 'reusable', 'reuse', 'responsible'], blurb: 'Customers praise the repairable, recyclable design and being able to replace a part rather than bin the whole thing.' },
        { label: 'Quality', sentiment: 'positive', count: 4, keywords: ['quality', 'well made', 'well-made', 'sturdy', 'solid', 'durable', 'built to last'], blurb: 'Customers find the headphones well made and built to last.' },
        { label: 'Design', sentiment: 'positive', count: 4, keywords: ['design', 'beautiful', 'colour', 'color'], blurb: 'Customers appreciate the thoughtful, modern design and the look of the headphones.' },
        { label: 'Customer service', sentiment: 'positive', count: 3, keywords: ['service', 'support', 'response', 'replacement', 'email', 'team', 'staff', 'help'], blurb: 'Customers highlight quick, personal and genuinely helpful support from the Kibu team.' },
        { label: 'Learning', sentiment: 'positive', count: 2, keywords: ['learn', 'educat', 'stem', 'teach', 'lesson'], blurb: 'Parents value the hands-on, educational experience and the lesson in circular design.' },
        { label: 'Sound', sentiment: 'positive', count: 2, keywords: ['sound', 'audio', 'volume', 'loud', 'listen', 'noise'], blurb: 'Customers are happy with the sound, and reassured by the built-in volume limit.' },
    ],
    updatedAt: '2026-06-23',
}
